import { ChangeEventHandler, useMemo, useState } from 'react'

import { isEqual } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { SingleValue } from 'react-select'
import { connect, DataItem, useDataItem } from 'scrivito'

import { ActivitySublistItem } from './ActivitySublistItem'
import { ActivityTabFilters } from './ActivityTabFilters'
import { useActivityTabData } from './useActivityTabData'
import { LIST_SIZE } from '../../const'
import { TabEmptyState } from '../TabEmptyState/TabEmptyState'
import { StringText } from '@/Atomic/Atoms/StringText'
import { Button } from '@/Atomic/Molecules/Button'
import { Search } from '@/Atomic/Molecules/Inputs/Search'
import { Option } from '@/Atomic/Molecules/Inputs/Select/types'
import { SimpleSelect } from '@/Atomic/Molecules/SimpleSelect'
import { ScrollablePanel } from '@/Atomic/Templates/ScrollablePanel'
import { Sublist, SublistFooter } from '@/Atomic/Templates/Sublist'
import { TimelineSection } from '@/Atomic/Templates/TimelineSection'
import { DataClassActivityDrawer } from '@/Atomic/Widgets/DataClassDrawer/DataClass.ActivityDrawer'
import { DataItemDrawer } from '@/Atomic/Widgets/DataItemDrawer'
import { AutomationEventSublistItem } from '@/Components/Activity/AutomationEventSublistItem/AutomationEventSublistItem'
import { groupAutomationEvents } from '@/Components/Activity/AutomationEventSublistItem/groupAutomationEvents'
import { type FormValues } from '@/Components/FormContext'
import { errorToast } from '@/Components/Toast'
import { ActivityContactRelation } from '@/Data/ActivityContactRelation/ActivityContactRelationDataClass'
import { ActivityDocumentRelation } from '@/Data/ActivityDocumentRelation/ActivityDocumentRelationDataClass'
import { ActivityProjectRelation } from '@/Data/ActivityProjectRelation/ActivityProjectRelationDataClass'
import {
  ACTIVITY_TAB_ASC_ORDER,
  ACTIVITY_TAB_DESC_ORDER,
  DataClassName,
} from '@/Data/const'
import { DataScopeOrder } from '@/Data/types'
import { useCanvas } from '@/hooks/useCanvas'
import { useFlashSublistItem } from '@/hooks/useFlashSublistItem'
import { useDataItemDrawer } from '@/utils/CRUD/hooks/useDataItemDrawer'
import { getEntityLabelKey } from '@/utils/getEntityLabelKey'
import { reportError } from '@/utils/reportError'
import {
  groupItemsByTimelineDate,
  parseDateAttribute,
} from '@/utils/timelineDateGroups'

import './ActivitySublistItem.scss'

interface ActivityTabProps {
  searchInputName: string
  activityRelation?: 'contact' | 'project' | 'document'
}

export const ActivityTab: React.FC<ActivityTabProps> = connect(
  ({ searchInputName, activityRelation = 'contact' }) => {
    const { t } = useTranslation()
    const dataItem = useDataItem()
    const subjectId = dataItem?.id() ?? ''

    const [search, setSearch] = useState('')
    const [order, setOrder] = useState<DataScopeOrder>(ACTIVITY_TAB_DESC_ORDER)
    const [filters, setFilters] = useState<FormValues>({})
    const hasActiveFilters = Object.keys(filters).length > 0

    const {
      drawerItem,
      setDrawerItem,
      isDrawerOpen,
      openDrawer,
      closeDrawer,
      startInEditMode,
    } = useDataItemDrawer()
    const { isCanvasOpen, openCanvas, closeCanvas } = useCanvas()
    const { flashId: flashItemId, flash: flashItemIdNow } =
      useFlashSublistItem()

    const onSearchChange: ChangeEventHandler<HTMLInputElement> = (event) => {
      setSearch(event.target.value)
    }

    const orderOptions: Option[] = [
      { label: t('common.sort.recentFirst'), value: 'desc' },
      { label: t('common.sort.recentLast'), value: 'asc' },
    ]
    const currentOrderValue = isEqual(order, ACTIVITY_TAB_DESC_ORDER)
      ? 'desc'
      : 'asc'
    const selectedOrderOption = orderOptions.find(
      (o) => o.value === currentOrderValue,
    )
    const handleOrderChange = (newValue: SingleValue<Option | unknown>) => {
      const val = (newValue as Option)?.value
      setOrder(val === 'asc' ? ACTIVITY_TAB_ASC_ORDER : ACTIVITY_TAB_DESC_ORDER)
    }

    const { items, hasMore, loadMore, count } = useActivityTabData({
      subjectId,
      search,
      order,
      listSize: LIST_SIZE,
      activityRelation,
      type: filters['type'],
    })

    const timelineGroups = useMemo(
      () =>
        groupItemsByTimelineDate({
          items,
          getDate: (item) => parseDateAttribute(item.activity.get('updatedAt')),
          t,
        }),
      [items, t],
    )

    const onActivityCreated = async (newActivity: DataItem) => {
      try {
        if (activityRelation === 'project') {
          await ActivityProjectRelation.create({
            project: subjectId,
            activity: newActivity.id(),
          })
        } else if (activityRelation === 'document') {
          await ActivityDocumentRelation.create({
            document: subjectId,
            activity: newActivity.id(),
          })
        } else {
          await ActivityContactRelation.create({
            contact: subjectId,
            activity: newActivity.id(),
          })
        }
        flashItemIdNow(newActivity.id())
      } catch (error) {
        errorToast(error as Error)
        reportError(error)
      } finally {
        closeCanvas()
      }
    }

    if (!subjectId) return

    const header = (
      <div className="tab-header-wrapper">
        <div className="search-filter">
          <Search
            name={searchInputName}
            onChange={onSearchChange}
            searchWrapperClassName="tab-header-search"
            placeholder={t('search.title')}
          />
          <ActivityTabFilters values={filters} onChange={setFilters} />
          <StringText value={t('common.sortBy')} />
          <SimpleSelect
            name="order-select"
            className="small"
            options={orderOptions}
            value={selectedOrderOption}
            onChange={handleOrderChange}
          />
        </div>
        <div className="tab-header-actions">
          <Button
            title={t('common.create')}
            size="sm"
            iconSize="sm"
            iconColor="white"
            icon="add"
            onClick={openCanvas}
          />
        </div>
      </div>
    )

    return (
      <>
        <Sublist>
          <ScrollablePanel header={header}>
            {count === 0 ? (
              <div className="sublist-wrapper">
                {search || hasActiveFilters ? (
                  <TabEmptyState
                    title={t('tabs.noResults.title')}
                    description={t('tabs.noResults.searchCreate', {
                      entity: t(getEntityLabelKey(DataClassName.ACTIVITY), {
                        count: 1,
                      }),
                    })}
                    cta={{
                      label: t('common.create'),
                      icon: 'add',
                      onClick: openCanvas,
                    }}
                  />
                ) : (
                  <TabEmptyState
                    title={t('tabs.empty.activities.title')}
                    description={t('tabs.empty.activities.description')}
                    cta={{
                      label: t('common.create'),
                      icon: 'add',
                      onClick: openCanvas,
                    }}
                  />
                )}
              </div>
            ) : (
              <div className="sublist-wrapper activity-timeline">
                {timelineGroups.map((group) => (
                  <TimelineSection key={group.id} title={group.label}>
                    {groupAutomationEvents(group.items).map((entry) =>
                      entry.kind === 'automation' ? (
                        <AutomationEventSublistItem
                          key={entry.id}
                          activities={entry.items.map((item) => item.activity)}
                        />
                      ) : (
                        <ActivitySublistItem
                          key={entry.id}
                          activity={entry.item.activity}
                          flash={entry.item.id === flashItemId}
                          onOpenDrawer={(activity, shouldStartInEditMode) => {
                            setDrawerItem(activity)
                            openDrawer(shouldStartInEditMode)
                          }}
                        />
                      ),
                    )}
                  </TimelineSection>
                ))}
                <SublistFooter
                  amount={items.length}
                  total={count}
                  labelKey={getEntityLabelKey(DataClassName.ACTIVITY)}
                  onLoadMore={hasMore ? loadMore : undefined}
                />
              </div>
            )}
          </ScrollablePanel>
        </Sublist>
        {drawerItem && (
          <DataItemDrawer
            key={drawerItem.id()}
            isOpen={isDrawerOpen}
            close={closeDrawer}
            dataItem={drawerItem}
            startInEditMode={startInEditMode}
          />
        )}
        <DataClassActivityDrawer
          isOpen={isCanvasOpen}
          close={closeCanvas}
          onCreated={onActivityCreated}
        />
      </>
    )
  },
)
